/**
 * importQuestionsCsv.js
 * CSV import utility for quiz questions with validation and error handling
 * Parses CSV files and transforms them into question objects with proper validation
 */

import { v4 as uuidv4 } from 'uuid';
import { arrayOfObjects } from 'utils/jsonParser';
import {
  QUESTION_TYPES,
  QUESTION_TYPE_NAME_TO_ID,
  MULTIPLE_CHOICE_NAME,
  ESSAY_NAME,
} from 'utils/constants/question';

/**
 * Expected CSV headers for import (excludes id, subQuestQuizId, position)
 */
export const EXPECTED_HEADERS = [
  'questionTitle',
  'explanation',
  'autoCheckInstruction',
  'multipleChoiceChoicesJson',
  'multipleChoiceAnswersJson',
  'allowPartialPoints',
  'isAllowedAutoCheck',
  'mark',
  'questionTypeName',
  // 'questionCategories', // for future use
];

export const REQUIRED_HEADERS = ['questionTitle', 'mark'];

/**
 * Validates CSV headers against expected and required headers
 * @param {Array} headers - Array of header strings from CSV
 * @returns {Array} - Array of header validation error messages
 */
function validateHeaders(headers) {
  const errors = [];

  REQUIRED_HEADERS.forEach((requiredHeader) => {
    if (!headers.includes(requiredHeader)) {
      errors.push(`Missing required header: "${requiredHeader}"`);
    }
  });

  headers.forEach((header) => {
    if (!EXPECTED_HEADERS.includes(header)) {
      const suggestion = EXPECTED_HEADERS.find(
        (expected) =>
          expected.toLowerCase() === header.toLowerCase() ||
          expected.toLowerCase().includes(header.toLowerCase()) ||
          header.toLowerCase().includes(expected.toLowerCase())
      );

      if (suggestion) {
        errors.push(
          `Unexpected header: "${header}". Did you mean "${suggestion}"?`
        );
      } else {
        errors.push(
          `Unexpected header: "${header}". Expected headers are: ${EXPECTED_HEADERS.join(', ')}`
        );
      }
    }
  });

  return errors;
}

/**
 * Maps question type name to question type ID
 * @param {string} typeName - The question type name
 * @returns {number|null} - The question type ID or null if not found
 */
function getQuestionTypeIdByName(typeName) {
  if (!typeName) return null;
  return QUESTION_TYPE_NAME_TO_ID[typeName.toLowerCase().trim()] ?? null;
}

/**
 * Validates if a value is valid JSON
 * @param {string} jsonString - String to validate as JSON
 * @returns {boolean} - True if valid JSON, false otherwise
 */
function isValidJson(jsonString) {
  if (!jsonString || jsonString.trim() === '') return false;
  try {
    JSON.parse(jsonString);
    return true;
  } catch {
    return false;
  }
}

/**
 * Safely parses JSON arrays of objects with fallback to default value
 * @param {string} jsonString - JSON string to parse
 * @param {Array} defaultValue - Default value if parsing fails
 * @returns {Array} - Parsed array of objects or default value
 */
function safeJsonParse(jsonString, defaultValue = []) {
  if (!jsonString || jsonString.trim() === '') return defaultValue;
  return arrayOfObjects(jsonString);
}

/**
 * Validates a single question row based on question type requirements
 * @param {Object} row - The question row data
 * @param {number} rowIndex - The row index for error reporting
 * @returns {Array} - Array of validation error messages
 */
function validateQuestionRow(row, rowIndex) {
  const errors = [];
  const rowNumber = rowIndex + 1;

  if (!row.questionTitle || row.questionTitle.trim() === '') {
    errors.push(
      `Row ${rowNumber}: questionTitle is required and cannot be empty`
    );
  }

  if (!row.mark || row.mark.toString().trim() === '') {
    errors.push(`Row ${rowNumber}: mark is required`);
  } else {
    const markValue = parseFloat(row.mark);
    if (Number.isNaN(markValue)) {
      errors.push(`Row ${rowNumber}: mark must be a valid number`);
    } else if (markValue <= 0) {
      errors.push(`Row ${rowNumber}: mark must be greater than 0`);
    } else if (markValue > 100) {
      errors.push(`Row ${rowNumber}: mark cannot exceed 100`);
    }
  }

  if (!row.questionTypeName) {
    errors.push(`Row ${rowNumber}: questionTypeName is required`);
  } else {
    const typeName = row.questionTypeName.trim();
    const validTypeNames = QUESTION_TYPES.map((type) => type.Name);
    if (!validTypeNames.includes(typeName)) {
      errors.push(
        `Row ${rowNumber}: questionTypeName must be one of: ${validTypeNames.join(', ')}. Got: "${typeName}"`
      );
    }
  }

  if (row.allowPartialPoints !== undefined && row.allowPartialPoints !== '') {
    const value = row.allowPartialPoints.toString().toLowerCase();
    if (value !== 'true' && value !== 'false') {
      errors.push(
        `Row ${rowNumber}: allowPartialPoints must be "true" or "false". Got: "${row.allowPartialPoints}"`
      );
    }
  }

  if (row.isAllowedAutoCheck !== undefined && row.isAllowedAutoCheck !== '') {
    const value = row.isAllowedAutoCheck.toString().toLowerCase();
    if (value !== 'true' && value !== 'false') {
      errors.push(
        `Row ${rowNumber}: isAllowedAutoCheck must be "true" or "false". Got: "${row.isAllowedAutoCheck}"`
      );
    }
  }

  // Type-specific validation
  if (row.questionTypeName === MULTIPLE_CHOICE_NAME) {
    if (
      !row.multipleChoiceChoicesJson ||
      row.multipleChoiceChoicesJson.trim() === ''
    ) {
      errors.push(
        `Row ${rowNumber}: Multiple Choice questions require multipleChoiceChoicesJson`
      );
    } else {
      const choices = arrayOfObjects(row.multipleChoiceChoicesJson);
      if (choices.length === 0) {
        errors.push(
          `Row ${rowNumber}: multipleChoiceChoicesJson must be a valid JSON array of objects`
        );
      } else if (choices.length < 2) {
        errors.push(
          `Row ${rowNumber}: Multiple Choice questions must have at least 2 choices`
        );
      } else {
        choices.forEach((choice, index) => {
          if (!choice.option || !choice.value) {
            errors.push(
              `Row ${rowNumber}: Choice ${index + 1} must have both "option" and "value" properties`
            );
          }
        });
      }
    }

    if (
      !row.multipleChoiceAnswersJson ||
      row.multipleChoiceAnswersJson.trim() === ''
    ) {
      errors.push(
        `Row ${rowNumber}: Multiple Choice questions require multipleChoiceAnswersJson`
      );
    } else {
      const answers = arrayOfObjects(row.multipleChoiceAnswersJson);
      if (answers.length === 0) {
        errors.push(
          `Row ${rowNumber}: multipleChoiceAnswersJson must be a valid JSON array of objects with at least 1 correct answer`
        );
      } else {
        const choices = arrayOfObjects(row.multipleChoiceChoicesJson);
        if (choices.length > 0) {
          const choiceValues = choices.map((choice) => choice.value);

          answers.forEach((answer) => {
            if (!answer || typeof answer !== 'object' || !answer.value) {
              errors.push(
                `Row ${rowNumber}: Answer must be an object with a "value" property`
              );
            } else if (!choiceValues.includes(answer.value)) {
              errors.push(
                `Row ${rowNumber}: Answer "${answer.value}" does not match any choice value`
              );
            }
          });
        }
      }
    }
  }

  if (row.questionTypeName === ESSAY_NAME) {
    if (!row.questionTitle || row.questionTitle.trim() === '') {
      errors.push(`Row ${rowNumber}: Essay questions require questionTitle`);
    }

    if (row.isAllowedAutoCheck === 'true' || row.isAllowedAutoCheck === true) {
      if (!row.autoCheckInstruction || row.autoCheckInstruction.trim() === '') {
        errors.push(
          `Row ${rowNumber}: Essay questions with auto-check enabled require autoCheckInstruction`
        );
      }
    }
  }

  return errors;
}

/**
 * Transforms a CSV row into a question object with proper defaults
 * @param {Object} row - The CSV row data
 * @param {number} position - The position index for the question
 * @returns {Object} - Transformed question object
 */
function transformRowToQuestion(row, position) {
  const questionTypeId = getQuestionTypeIdByName(row.questionTypeName);

  // Set default mark to 1 if not provided or invalid
  let defaultMark = row.mark ? parseInt(row.mark, 10) : null;
  if (!defaultMark || Number.isNaN(defaultMark) || defaultMark < 1) {
    defaultMark = 1;
  }

  return {
    id: null,
    tempId: uuidv4(),
    questionHtml: row.questionTitle || '',
    explanation: row.explanation || '',
    autoCheckInstruction: row.autoCheckInstruction || '',
    multipleChoiceChoices: safeJsonParse(row.multipleChoiceChoicesJson, []),
    multipleChoiceAnswers: safeJsonParse(row.multipleChoiceAnswersJson, []),
    allowPartialPoints:
      row.allowPartialPoints.toLowerCase() === 'true' ||
      row.allowPartialPoints === true,
    isAllowedAutoCheck:
      row.isAllowedAutoCheck.toLowerCase() === 'true' ||
      row.isAllowedAutoCheck === true,
    mark: defaultMark,
    questionTypeId,
    position,
    trueOrFalseAnswer: true,
    errors: [],
  };
}

/**
 * Parses CSV text content into an array of objects
 * @param {string} csvText - The CSV file content as text
 * @returns {Array} - Array of row objects
 */
function parseCsvText(csvText) {
  const lines = csvText
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line);

  if (lines.length === 0) {
    throw new Error('CSV file is empty');
  }

  // Parse headers
  const headerLine = lines[0];
  const headers = headerLine
    .split(',')
    .map((header) => header.trim().replace(/"/g, ''));

  // Parse data rows
  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const line = lines[i];
    if (line.trim()) {
      // CSV parsing with proper escaped double-quote handling
      const values = [];
      let currentValue = '';
      let inQuotes = false;

      for (let j = 0; j < line.length; j += 1) {
        const char = line[j];

        if (char === '"') {
          // If inside a quoted field and next char is also a quote, it's an escaped quote
          if (inQuotes && j + 1 < line.length && line[j + 1] === '"') {
            currentValue += '"'; // Add literal quote
            j += 1; // Skip the second quote
          } else {
            // Toggle quote state (start/end of quoted field)
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          values.push(currentValue);
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
      values.push(currentValue); // Add the last value

      // Create row object
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });

      // Normalize Excel-style quoted JSON fields (strip outer quotes and un-escape doubled quotes)
      ['multipleChoiceChoicesJson', 'multipleChoiceAnswersJson'].forEach(
        (field) => {
          const raw = row[field];
          if (raw !== undefined && raw !== '') {
            let normalized = raw;
            if (normalized.startsWith('"') && normalized.endsWith('"')) {
              normalized = normalized.slice(1, -1);
            }
            if (normalized.indexOf('""') !== -1) {
              normalized = normalized.replace(/""/g, '"');
            }

            // If normalized is valid JSON, replace the raw field so later validation/transform uses it
            if (isValidJson(normalized)) {
              row[field] = normalized;
            }
          }
        }
      );

      rows.push(row);
    }
  }

  return rows;
}

/**
 * Main function to import and validate questions from CSV file
 * @param {File} file - The CSV file to import
 * @returns {Promise<Object>} - Promise resolving to { questions, errors, summary }
 */
export default async function importQuestionsFromCsv(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'));
      return;
    }

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      reject(new Error('Please select a CSV file'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      reject(new Error('File size must be less than 5MB'));
      return;
    }

    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const csvText = event.target.result;
        const rows = parseCsvText(csvText);

        const questions = [];
        const allErrors = [];
        let validQuestionCount = 0;

        if (rows.length > 0) {
          const headers = Object.keys(rows[0]);
          const headerErrors = validateHeaders(headers);
          allErrors.push(...headerErrors);

          const hasCriticalHeaderErrors = headerErrors.some((error) =>
            error.includes('Missing required header')
          );

          if (!hasCriticalHeaderErrors) {
            rows.forEach((row, index) => {
              const rowErrors = validateQuestionRow(row, index);

              if (rowErrors.length === 0) {
                const question = transformRowToQuestion(row, index);
                questions.push(question);
                validQuestionCount += 1;
              } else {
                allErrors.push(...rowErrors);
              }
            });
          }
        }

        // Create summary
        const summary = {
          totalRows: rows.length,
          validQuestions: validQuestionCount,
          invalidQuestions: rows.length - validQuestionCount,
          hasErrors: allErrors.length > 0,
        };

        resolve({
          questions,
          errors: allErrors,
          summary,
        });
      } catch (error) {
        reject(new Error(`Failed to parse CSV file: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file);
  });
}
